import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { 
  RenewalDashboardState, 
  RenewalDashboardMetrics, 
  RenewalDashboardFilters,
  RenewalDashboardFilterOptions,
  ChartData,
  RenewalPolicy,
  RenewalPoliciesResponse,
  RenewalPerformanceStats
} from '@/types/renewalDashboard';

const initialState: RenewalDashboardState = {
  // Metrics
  metrics: null,
  metricsLoading: false,
  metricsError: null,
  
  // Filters
  filters: null,
  filtersLoading: false,
  filtersError: null,
  
  // Charts
  smsSuccessChart: null,
  expiryDistributionChart: null,
  monthTrendChart: null,
  chartsLoading: false,
  chartsError: null,
  
  // Policies
  policies: [],
  policiesLoading: false,
  policiesError: null,
  policiesPagination: {
    total: 0,
    page: 1,
    page_size: 50,
    total_pages: 0
  },
  
  // Current filters
  currentFilters: {} as RenewalDashboardFilterOptions,
  
  // UI State
  isFiltersOpen: true, // Filters always visible by default
  isPoliciesOpen: false,
  
  // Performance
  performanceStats: null,
  performanceLoading: false,
  performanceError: null
};

const renewalDashboardSlice = createSlice({
  name: 'renewalDashboard',
  initialState,
  reducers: {
    // Metrics Actions
    fetchRenewalMetricsRequested: (state, action: PayloadAction<RenewalDashboardFilterOptions>) => {
      state.metricsLoading = true;
      state.metricsError = null;
      state.currentFilters = action.payload;
    },
    fetchRenewalMetricsSucceeded: (state, action: PayloadAction<RenewalDashboardMetrics>) => {
      state.metricsLoading = false;
      state.metrics = action.payload;
    },
    fetchRenewalMetricsFailed: (state, action: PayloadAction<string>) => {
      state.metricsLoading = false;
      state.metricsError = action.payload;
    },
    
    // Filters Actions
    fetchRenewalFiltersRequested: (state) => {
      state.filtersLoading = true;
      state.filtersError = null;
    },
    fetchRenewalFiltersSucceeded: (state, action: PayloadAction<RenewalDashboardFilters>) => {
      state.filtersLoading = false;
      state.filters = action.payload;
    },
    fetchRenewalFiltersFailed: (state, action: PayloadAction<string>) => {
      state.filtersLoading = false;
      state.filtersError = action.payload;
    },
    
    // Charts Actions
    fetchRenewalChartsRequested: (state, action: PayloadAction<{chartType: string, filters: RenewalDashboardFilterOptions}>) => {
      state.chartsLoading = true;
      state.chartsError = null;
    },
    fetchRenewalChartsSucceeded: (state, action: PayloadAction<{chartType: string, data: ChartData}>) => {
      state.chartsLoading = false;
      const { chartType, data } = action.payload;
      switch (chartType) {
        case 'sms-success-rate':
          state.smsSuccessChart = data;
          break;
        case 'expiry-distribution':
          state.expiryDistributionChart = data;
          break;
        case 'month-wise-trend':
          state.monthTrendChart = data;
          break;
      }
    },
    fetchRenewalChartsFailed: (state, action: PayloadAction<string>) => {
      state.chartsLoading = false;
      state.chartsError = action.payload;
    },
    
    // Policies Actions
    fetchRenewalPoliciesRequested: (state, action: PayloadAction<{filters: RenewalDashboardFilters, pagination: any}>) => {
      state.policiesLoading = true;
      state.policiesError = null;
    },
    fetchRenewalPoliciesSucceeded: (state, action: PayloadAction<RenewalPoliciesResponse>) => {
      state.policiesLoading = false;
      state.policies = action.payload.policies;
      state.policiesPagination = {
        total: action.payload.total,
        page: action.payload.page,
        page_size: action.payload.page_size,
        total_pages: action.payload.total_pages
      };
    },
    fetchRenewalPoliciesFailed: (state, action: PayloadAction<string>) => {
      state.policiesLoading = false;
      state.policiesError = action.payload;
    },
    
    // Performance Actions
    fetchRenewalPerformanceRequested: (state) => {
      state.performanceLoading = true;
      state.performanceError = null;
    },
    fetchRenewalPerformanceSucceeded: (state, action: PayloadAction<RenewalPerformanceStats>) => {
      state.performanceLoading = false;
      state.performanceStats = action.payload;
    },
    fetchRenewalPerformanceFailed: (state, action: PayloadAction<string>) => {
      state.performanceLoading = false;
      state.performanceError = action.payload;
    },
    
    // Refresh Actions
    refreshRenewalDashboardRequested: (state) => {
      state.metricsLoading = true;
      state.chartsLoading = true;
      state.metricsError = null;
      state.chartsError = null;
    },
    refreshRenewalDashboardSucceeded: (state) => {
      state.metricsLoading = false;
      state.chartsLoading = false;
    },
    refreshRenewalDashboardFailed: (state, action: PayloadAction<string>) => {
      state.metricsLoading = false;
      state.chartsLoading = false;
      state.metricsError = action.payload;
    },
    
    // UI State Actions
    openFiltersPanel: (state) => {
      state.isFiltersOpen = true;
    },
    closeFiltersPanel: (state) => {
      state.isFiltersOpen = false;
    },
    
    openPoliciesPanel: (state) => {
      state.isPoliciesOpen = true;
    },
    closePoliciesPanel: (state) => {
      state.isPoliciesOpen = false;
    },
    
    setCurrentFilters: (state, action: PayloadAction<RenewalDashboardFilterOptions>) => {
      state.currentFilters = action.payload;
    },
    
    clearCurrentFilters: (state) => {
      state.currentFilters = {} as RenewalDashboardFilterOptions;
    },
    
    clearRenewalDashboardError: (state) => {
      state.metricsError = null;
      state.filtersError = null;
      state.chartsError = null;
      state.policiesError = null;
      state.performanceError = null;
    },
    
    resetRenewalDashboard: (state) => {
      Object.assign(state, initialState);
    }
  }
});

export const {
  // Metrics
  fetchRenewalMetricsRequested,
  fetchRenewalMetricsSucceeded,
  fetchRenewalMetricsFailed,
  
  // Filters
  fetchRenewalFiltersRequested,
  fetchRenewalFiltersSucceeded,
  fetchRenewalFiltersFailed,
  
  // Charts
  fetchRenewalChartsRequested,
  fetchRenewalChartsSucceeded,
  fetchRenewalChartsFailed,
  
  // Policies
  fetchRenewalPoliciesRequested,
  fetchRenewalPoliciesSucceeded,
  fetchRenewalPoliciesFailed,
  
  // Performance
  fetchRenewalPerformanceRequested,
  fetchRenewalPerformanceSucceeded,
  fetchRenewalPerformanceFailed,
  
  // Refresh
  refreshRenewalDashboardRequested,
  refreshRenewalDashboardSucceeded,
  refreshRenewalDashboardFailed,
  
  // UI State
  openFiltersPanel,
  closeFiltersPanel,
  openPoliciesPanel,
  closePoliciesPanel,
  setCurrentFilters,
  clearCurrentFilters,
  clearRenewalDashboardError,
  resetRenewalDashboard
} = renewalDashboardSlice.actions;

export default renewalDashboardSlice.reducer;
